import type { FC } from 'react';
import { useCallback, useEffect } from 'react';
import type { UrlObject } from 'url';
import type { Analytics, ChampionshipSB } from '@mwl/core-lib';
import { loadChampionshipsRequest, selectChampionships, selectChampionshipsIsLoading, selectUserCurrency, useItemsFilteredByStage, useSportUrlParams } from '@mwl/core-lib';
import { Loader } from '@/components/Loader/Loader';
import { routes } from '@/constants';
import { useTypedDispatch, useTypedSelector } from '@/hooks';
import { ChampionshipItem } from '../ChampionshipItem/ChampionshipItem';
import { getChampionshipListAnalytics } from './ChampionshipList.analytics';
import type { ChampionshipListProps } from './ChampionshipList.types';
import styles from './ChampionshipList.module.scss';
export const ChampionshipList: FC<ChampionshipListProps> = ({
  categoryId: categoryIdProps
}) => {
  const dispatch = useTypedDispatch();
  const currency = useTypedSelector(selectUserCurrency);
  const data = useTypedSelector(selectChampionships);
  const isLoading = useTypedSelector(selectChampionshipsIsLoading);
  const {
    sportId,
    categoryId,
    championshipId,
    createChampionshipLink
  } = useSportUrlParams({
    routes
  });
  const {
    stage
  } = useSportUrlParams();
  const show = categoryId === categoryIdProps;
  const isActive = useCallback(({
    id
  }: ChampionshipSB) => {
    return championshipId === id;
  }, [championshipId]);
  const getAnalyticsData = useCallback((championship: ChampionshipSB, championships: Array<ChampionshipSB>, href: UrlObject): Analytics<'click'> => {
    const {
      championshipAnalytics
    } = getChampionshipListAnalytics({
      id: championship.id,
      sportId: championship.sportId,
      categoryId: categoryId ?? '',
      href,
      title: championship.title,
      championships
    });
    if (isActive(championship)) {
      return {
        click: {
          eventName: championshipAnalytics.unselect?.eventName,
          data: championshipAnalytics.unselect?.data
        }
      };
    }
    return {
      click: {
        eventName: championshipAnalytics.select?.eventName,
        data: championshipAnalytics.select?.data
      }
    };
  }, [categoryId, isActive]);
  const filteredChampionships = useItemsFilteredByStage(data || [], stage);
  useEffect(() => {
    if (show && currency) {
      dispatch(loadChampionshipsRequest({
        sportIds: sportId ? [sportId] : [],
        categoryIds: categoryId ? [categoryId] : []
      }));
    }
  }, [categoryId, currency, dispatch, show, sportId]);
  if (!show) {
    return null;
  }
  return <div className={styles.root} data-sentry-component="ChampionshipList" data-sentry-source-file="ChampionshipList.tsx">
      {isLoading && <div className={styles.loaderWrapper}>
          <Loader variant="sport" className={styles.loader} />
        </div>}

      {!isLoading && filteredChampionships.map(item => {
      const href = createChampionshipLink(item);
      return <ChampionshipItem key={item.id} item={item} isActive={isActive(item)} analytics={getAnalyticsData(item, filteredChampionships, href)} />;
    })}
    </div>;
};